import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { api } from '~/trpc-extra/api';
import { filterDynamicRouteParams } from './utils/routeUtils';

// Build query string from filtered query params
const buildQueryString = (query: Record<string, string | string[] | undefined>): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, v));
    } else {
      params.append(key, value);
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

type UseDefaultProjectRedirectOptions = {
  targetPath: string;
};

type UseDefaultProjectRedirectResult = {
  isRedirecting: boolean;
  hasNoProjects: boolean;
};

export const useDefaultProjectRedirect = ({
  targetPath,
}: UseDefaultProjectRedirectOptions): UseDefaultProjectRedirectResult => {
  const router = useRouter();
  const { activeOrg } = useAuthData();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { data: project, isLoading } = api.project.getDefaultProject.useQuery(
    { teamId: activeOrg?.orgId || '' },
    { enabled: !!activeOrg?.orgId },
  );

  // Redirect to the project-scoped path once the default project is loaded.
  // Preserves existing query params (excluding dynamic route params).
  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (project && !isRedirecting) {
      setIsRedirecting(true);
      const filteredQuery = filterDynamicRouteParams(router.query, router.pathname) as Record<
        string,
        string | string[] | undefined
      >;
      const queryString = buildQueryString(filteredQuery);
      const redirectUrl = `/project/${project.id}/${targetPath}${queryString}`;
      // Reset redirecting state on navigation failure to allow retry
      router.push(redirectUrl).catch((error) => {
        console.error('Failed to redirect to default project:', error);
        setIsRedirecting(false);
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    // Do not include full router object here or you get unnecessary re-renders
  }, [project, router.isReady, router.pathname, JSON.stringify(router.query), targetPath, isRedirecting]);

  // Only determine "no projects" after auth/org is ready AND query has completed.
  // When activeOrg is undefined, the query is disabled (idle), so we treat that as "unknown".
  const isAuthReady = !!activeOrg?.orgId;
  const hasNoProjects = isAuthReady && !isLoading && !project;

  return {
    // Show loader while auth is loading OR query is loading OR redirect is in progress
    isRedirecting: !isAuthReady || isLoading || isRedirecting,
    hasNoProjects,
  };
};
